import { t, useOutsideClick } from '../../../../utils'
import styles from './_postCommentActionsMenu.module.scss'
import { useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose, faEllipsis } from '@fortawesome/free-solid-svg-icons'
import { useMutation } from '@apollo/client'
import {
  COMMUNITY_DELETE_POST_MUTATION,
  COMMUNITY_HIDE_POST_MUTATION,
  COMMUNITY_POST_QUERY,
  COMMUNITY_POSTS_QUERY,
  COMMUNITY_TOGGLE_FEATURED_POST_MUTATION,
  COMMUNITY_TOGGLE_PIN_POST_MUTATION,
  DELETE_COMMUNITY_COMMENT_MUTATION
} from '../../../../gql'
import { getOperationName } from 'apollo-boost'
import useErrorHandling from '../../../../utils/hooks/useErrorHandling'
import { COMMUNITY_HIDE_COMMENT_MUTATION } from '../../../../gql/mutations/community_hide_comment_mutation'
import { useAuth } from '../../../../utils/auth'
import ReportPostButton from './ReportPostButton'
import { CommunityContext } from './CommunityLayout'
import { CommunityPost } from '../../../../__generated__/graphql'

type PostActionsProps = {
  post: CommunityPost
  commentId?: never
  isPinned: boolean
  isFeatured: boolean
  onRemove: () => void
  onPinToggle?: () => void
  onMenuOpenChange?: (isOpen: boolean) => void
}

type CommentActionsProps = {
  commentId: number
  post?: never
  isPinned?: never
  isFeatured?: never
  onRemove?: never
  onPinToggle?: never
  onMenuOpenChange?: (isOpen: boolean) => void
}

type PostCommentActionsProps = PostActionsProps | CommentActionsProps

const PostCommentActionsMenu: React.FC<PostCommentActionsProps> = ({
  post,
  commentId,
  onRemove,
  onPinToggle,
  onMenuOpenChange,
  isPinned,
  isFeatured
}) => {
  const { isAuthenticated, isAdmin, currentUser } = useAuth()
  const isAuthor = post?.author.id === currentUser?.id
  const postId = post?.id

  const [isPostActionsMenuOpen, setIsPostActionsMenuOpen] = useState(false)
  const [isPostPinned, setIsPostPinned] = useState(isPinned)
  const [isPostFeatured, setIsPostFeatured] = useState(isFeatured)
  const menuContainerRef = useRef<HTMLDivElement>(null)

  const errorHandling = useErrorHandling()

  const [deletePost, { loading: loadingDeletePost }] = useMutation(
    COMMUNITY_DELETE_POST_MUTATION,
    {
      variables: { postId },
      refetchQueries: [getOperationName(COMMUNITY_POSTS_QUERY)],
      onCompleted: onRemove,
      onError: errorHandling
    }
  )

  const [hidePost, { loading: loadingHidePost }] = useMutation(
    COMMUNITY_HIDE_POST_MUTATION,
    {
      variables: { postId },
      refetchQueries: [getOperationName(COMMUNITY_POSTS_QUERY)],
      onCompleted: onRemove,
      onError: errorHandling
    }
  )

  const [deleteComment, { loading: loadingDeleteComment }] = useMutation(
    DELETE_COMMUNITY_COMMENT_MUTATION,
    {
      variables: { id: commentId },
      refetchQueries: [getOperationName(COMMUNITY_POST_QUERY)],
      awaitRefetchQueries: true,
      onError: errorHandling
    }
  )

  const [hideComment, { loading: loadingHideComment }] = useMutation(
    COMMUNITY_HIDE_COMMENT_MUTATION,
    {
      variables: { id: commentId },
      refetchQueries: [getOperationName(COMMUNITY_POST_QUERY)],
      awaitRefetchQueries: true,
      onError: errorHandling
    }
  )
  const [togglePinPost, { loading: loadingTogglePin }] = useMutation(
    COMMUNITY_TOGGLE_PIN_POST_MUTATION,
    {
      variables: { postId },
      onCompleted: ({ togglePinPost }) => {
        setIsPostPinned(togglePinPost.communityPost.pinned)
        onPinToggle?.()
      },
      refetchQueries: [
        getOperationName(COMMUNITY_POSTS_QUERY),
        getOperationName(COMMUNITY_POST_QUERY)
      ],
      awaitRefetchQueries: true,
      onError: errorHandling
    }
  )

  const [toggleFeaturedPost, { loading: loadingToggleFeaturedPost }] =
    useMutation(COMMUNITY_TOGGLE_FEATURED_POST_MUTATION, {
      variables: { postId },
      onCompleted: ({ toggleFeaturedPost }) => {
        setIsPostFeatured(toggleFeaturedPost.communityPost.featured)
      },
      refetchQueries: [
        getOperationName(COMMUNITY_POSTS_QUERY),
        getOperationName(COMMUNITY_POST_QUERY)
      ],
      awaitRefetchQueries: true,
      onError: errorHandling
    })

  const handleDelete = () => {
    if (postId) deletePost()
    else if (commentId) deleteComment()
  }

  const handleHide = () => {
    if (postId) hidePost()
    else if (commentId) hideComment()
  }

  const setMenuOpen = (isOpen: boolean) => {
    setIsPostActionsMenuOpen(isOpen)
    onMenuOpenChange?.(isOpen)
  }

  const handleOutsideClick = () => {
    if (isPostActionsMenuOpen) {
      setMenuOpen(false)
    }
  }

  useOutsideClick(menuContainerRef, isPostActionsMenuOpen, handleOutsideClick)

  return (
    <div className={styles.menuContainer} ref={menuContainerRef}>
      <button onClick={() => setMenuOpen(true)}>
        <FontAwesomeIcon
          className={styles.openMenuButton}
          icon={faEllipsis}
          color="#bababa"
        />
      </button>

      {isPostActionsMenuOpen && (
        <div className={styles.postActionsMenu}>
          <button
            className={styles.closeMenuButton}
            onClick={() => setMenuOpen(false)}
          >
            <FontAwesomeIcon
              className={styles.closeMenuButton}
              icon={faClose}
              color="#9B9B9B"
            />
          </button>

          <div className={styles.actionButtons}>
            {isAdmin && (
              <>
                <button
                  className="button lab-button lab-button-dark"
                  onClick={handleHide}
                  disabled={loadingHidePost || loadingHideComment}
                >
                  {t('Hide')}
                </button>
                <button
                  className="button lab-button lab-button-dark"
                  onClick={handleDelete}
                  disabled={loadingDeletePost || loadingDeleteComment}
                >
                  {t('Delete')}
                </button>

                <button
                  className="button lab-button lab-button-dark"
                  onClick={() => togglePinPost()}
                  disabled={loadingTogglePin}
                >
                  {isPostPinned ? t('Unpin') : t('Pin')}
                </button>
                <button
                  className="button lab-button lab-button-dark"
                  onClick={() => toggleFeaturedPost()}
                  disabled={loadingToggleFeaturedPost}
                >
                  {isPostFeatured ? t('Unfeature') : t('Feature')}
                </button>
              </>
            )}
            {postId && isAuthenticated && !isAuthor && (
              <ReportPostButton
                context={CommunityContext}
                post={post}
                onReportModalClose={() => setMenuOpen(false)}
                buttonsClassName="button lab-button lab-button-dark"
                buttonTextReport={t('Report')}
                buttonTextReported={t('Reported')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PostCommentActionsMenu
